import { format } from 'date-fns';
import {
    BurnedInAnnotationStatus,
    aggregateBurnedInAnnotation,
} from '~/domain/dicom/burnedInAnnotation';
import type { Image3D } from '~/domain/patient/image3d';
import type { PublishedFrame } from '~/domain/viewport/publishedFrame';
import type { CanvasFrameSource } from '~/engine/view/TextureView';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { Orientations } from '~/engine/view/views/PlaneView';
import { downloadCreatedFile } from '~/utils/io/files/handleFileExport';

/**
 * Capture the given plane view as a PNG blob.
 *
 * DOM-overlay PHI is excluded by construction: the view's {@link PlaneView.canvas}
 * is a `bitmaprenderer` canvas holding only the frame the engine composited from
 * same-origin WebGL buffers (background image, contour outlines, active display
 * overlay). The patient banner and the series description/date (ImageIdentifier)
 * are React/DOM elements layered ABOVE the canvas at a higher z-index — never
 * drawn into it — so reading the canvas cannot capture those. Because the pixels
 * are same-origin the canvas is untainted and `toBlob` does not throw a security
 * error.
 *
 * This does NOT make the pixels themselves PHI-free: the composited background is
 * the DICOM image, which may carry *burned-in* identifiers in its pixel data.
 * That pixel-level vector is gated separately by {@link downloadViewportScreenshot}
 * / {@link viewportBurnedInStatus}, not here — this function only serialises
 * whatever the canvas already holds.
 *
 * Returns null when the view has no canvas or the browser produces no blob.
 */
export async function captureViewportPng(
    view: PlaneView
): Promise<Blob | null> {
    const canvas = view.canvas;
    if (!canvas) {
        return null;
    }
    return new Promise<Blob | null>((resolve) =>
        canvas.toBlob((blob) => resolve(blob), 'image/png')
    );
}

/**
 * The burned-in status that governs whether one composited frame source may be
 * captured: its DICOM verdict, except that a source of unknown provenance
 * (`null`) is always {@link BurnedInAnnotationStatus.Unknown}, and an Unknown
 * source the user has already vetted this session (`reviewedImages`) counts as
 * `No`. A declared `Yes` is never downgraded. The single place the review
 * override is applied, so {@link viewportBurnedInStatus} and
 * {@link viewportImagesAwaitingReview} cannot drift apart.
 */
function effectiveBurnedInStatus(
    source: CanvasFrameSource,
    reviewedImages: ReadonlySet<Image3D>
): BurnedInAnnotationStatus {
    if (source === null) {
        return BurnedInAnnotationStatus.Unknown;
    }
    const status = source.burnedInAnnotationStatus;
    return status === BurnedInAnnotationStatus.Unknown &&
        reviewedImages.has(source)
        ? BurnedInAnnotationStatus.No
        : status;
}

/**
 * The fail-safe {@link BurnedInAnnotationStatus} of the DICOM image(s) composited
 * into the frame **currently published on `view`'s canvas** (a {@link PlaneView},
 * or a grid tile's {@link PublishedFrame} read through its renderer), covering both the
 * primary and any secondary (fused) image — so a PET/CT fusion is only "safe" when
 * BOTH datasets affirm they carry no burned-in identifiers. When no image has been
 * rendered there is no DICOM pixel data on the canvas, hence nothing to leak, so
 * the result is `No` (safe).
 *
 * Deliberately reads {@link PlaneView.canvasFrameSources} — the images the
 * engine committed alongside the on-canvas frame — rather than the live
 * `view.environment`. The two diverge for the ~1 frame between an image swap and
 * the async `createImageBitmap`/`transferFromImageBitmap` that publishes the new
 * frame (see {@link TextureView.renderLayers}); reading the environment there would
 * let the gate clear a freshly-selected safe image while the canvas still holds the
 * previous, possibly burned-in one.
 *
 * This is a point-in-time read: it describes the frame published *now*, and the
 * engine is free to publish another one immediately after. Callers that serialise
 * the canvas must therefore confirm the frame did not change under them — see
 * {@link downloadViewportScreenshot}, which brackets the async capture with a
 * {@link PlaneView.canvasFrameId} check — rather than treating one read as
 * covering the whole operation.
 *
 * `reviewedImages` are images the user has already visually verified this session
 * (see {@link ScreenshotTool}); each contributes `No` in place of `Unknown`, so a
 * confirmation is asked for once per image instead of once per capture. `Yes` is
 * never downgradable — a DICOM-declared burned-in image stays blocked no matter
 * what the user attests. Pass an empty set (the default) for the raw verdict,
 * which is what the hard gate uses.
 */
export function viewportBurnedInStatus(
    view: PublishedFrame,
    reviewedImages: ReadonlySet<Image3D> = new Set()
): BurnedInAnnotationStatus {
    const sources = view.canvasFrameSources;
    if (sources.length === 0) {
        return BurnedInAnnotationStatus.No;
    }
    return aggregateBurnedInAnnotation(
        sources.map((source) => effectiveBurnedInStatus(source, reviewedImages))
    );
}

/**
 * The images on `view`'s published frame the user would be attesting to if they
 * confirmed a capture now: those whose burned-in status cannot be affirmed from
 * DICOM and that they have not already reviewed this session.
 *
 * Returns an empty array when the frame's provenance is unknown — there is no
 * image to attach an attestation to, so such a frame prompts every time.
 */
export function viewportImagesAwaitingReview(
    view: PublishedFrame,
    reviewedImages: ReadonlySet<Image3D>
): Image3D[] {
    return view.canvasFrameSources.filter(
        (source): source is Image3D =>
            source !== null &&
            effectiveBurnedInStatus(source, reviewedImages) ===
                BurnedInAnnotationStatus.Unknown
    );
}

/**
 * Thrown by {@link downloadViewportScreenshot} when the viewport's image is DICOM-
 * flagged as containing burned-in patient identifiers (`BurnedInAnnotation` ==
 * YES). Capturing it would copy that PHI into a shareable PNG, so the capture is
 * refused outright — a hard backstop independent of any UI guard (the tool
 * blocks YES and confirms the Unknown case before ever calling in).
 */
export class BurnedInAnnotationError extends Error {
    public constructor(
        message: string = 'Viewport screenshot refused: the image is marked as ' +
            'containing burned-in patient information ' +
            '(DICOM BurnedInAnnotation = YES).'
    ) {
        super(message);
        this.name = 'BurnedInAnnotationError';
    }
}

/** File-name fragment naming the plane a screenshot was taken in. */
const ORIENTATION_FILENAME_LABELS: Record<Orientations, string> = {
    [Orientations.TRANSVERSAL_ORIENTATION]: 'transversal',
    [Orientations.SAGITTAL_ORIENTATION]: 'sagittal',
    [Orientations.CORONAL_ORIENTATION]: 'coronal',
};

/**
 * File name for a shared viewport screenshot, e.g.
 * `viewport-screenshot-transversal-roi-7-20260720-141530.png`.
 *
 * Deliberately PHI-free (SR-WOR-H-41-1): it carries only the captured plane, the
 * structure-set-local ROI number, and the wall-clock time at which the user
 * pressed the button — never a patient name/ID, DICOM UID, accession number,
 * study/series description, or any date carried in the DICOM data. The structure
 * set label is *not* included: it is operator-authored free text and sites do put
 * patient identifiers in it.
 *
 * When the plane views are rotated to an oblique orientation the name says
 * `oblique` INSTEAD of a plane label. `orientation` is only the view's
 * *label* — it keeps reading `transversal` however far the frame is tilted —
 * so naming an oblique capture after it would assert an anatomical plane the
 * image is not on. That is the same label-versus-geometry hazard the
 * contouring gate exists to prevent, and it matters more here than on screen:
 * the file leaves the application, and the on-screen OBLIQUE badge cannot
 * travel with it (the capture is the WebGL canvas alone, by the deliberate
 * DOM-exclusion design documented above, so no overlay is ever in the pixels).
 * For the same reason a capture whose pixels hold an operation preview says
 * `preview`: the result is not applied, and the on-screen PREVIEW statement
 * is not in them.
 *
 * `capturedAt` is injected rather than read from the clock here so the name is
 * deterministic and testable.
 */
export function viewportScreenshotFilename({
    orientation,
    isOblique,
    showsUnappliedPreview,
    roiNumber,
    capturedAt,
}: ViewportScreenshotOptions): string {
    const parts = [
        'viewport-screenshot',
        isOblique ? 'oblique' : ORIENTATION_FILENAME_LABELS[orientation],
        ...(showsUnappliedPreview ? ['preview'] : []),
        ...(roiNumber === undefined ? [] : [`roi-${roiNumber}`]),
        format(capturedAt, 'yyyyMMdd-HHmmss'),
    ];
    return `${parts.join('-')}.png`;
}

/** Everything {@link downloadViewportScreenshot} needs beyond the view itself. */
export interface ViewportScreenshotOptions {
    /** Plane shown in the captured viewport; folded into the file name. */
    orientation: Orientations;
    /**
     * Whether the plane views are rotated off their patient-aligned
     * orientation. When true the file name says `oblique` rather than naming
     * a plane the capture is not actually on — see
     * {@link viewportScreenshotFilename}.
     */
    isOblique: boolean;
    /**
     * Whether the captured image holds an operation preview, a result not
     * yet applied. When true the file name says `preview` (see
     * {@link viewportScreenshotFilename}).
     */
    showsUnappliedPreview: boolean;
    /**
     * Structure-set-local ROI number of the selected structure, or undefined when
     * no structure is selected. Folded into the file name only.
     */
    roiNumber: number | undefined;
    /** Wall-clock time the user requested the capture; folded into the file name. */
    capturedAt: Date;
}

/**
 * Capture the frame currently shown in `view` and trigger a browser download of
 * the PNG.
 *
 * PHI gate: refuses with {@link BurnedInAnnotationError} when the image is DICOM-
 * flagged `BurnedInAnnotation` == YES, so a known-unsafe image can never be
 * serialised regardless of the caller. The Unknown case (attribute absent) is
 * NOT blocked here — the caller must obtain explicit user confirmation first
 * (see {@link ScreenshotTool}); an affirmed `No` exports freely.
 *
 * The capture is bound to a single published frame. The engine can publish a
 * *new* frame while `toBlob` is in flight (see {@link viewportBurnedInStatus}),
 * and its pixels — not the ones the gate cleared — are what land in the blob. A
 * monotonic {@link PlaneView.canvasFrameId} is committed with each frame, so we
 * read it before the capture and refuse if it has moved by the time `toBlob`
 * returns: we then cannot say which frame was serialised. This is strictly
 * stronger than comparing the burned-in *verdict*, which a same-verdict swap to a
 * *different* (unvetted) image — or a No→Yes→No sequence within the window —
 * would slip past unchanged.
 *
 * Throws when no image can be produced (no canvas, or the browser serialises the
 * canvas to a null blob). This tool is user-invoked and is expected to save a
 * file, so a failed capture must surface to the caller as an error rather than
 * silently doing nothing — {@link ScreenshotTool} reports it via `handleError`.
 */
export async function downloadViewportScreenshot(
    view: PlaneView,
    options: ViewportScreenshotOptions
): Promise<void> {
    // Refuse a DICOM-declared YES outright, before doing any work.
    if (viewportBurnedInStatus(view) === BurnedInAnnotationStatus.Yes) {
        throw new BurnedInAnnotationError();
    }
    // Bind the bytes to one frame: if the engine republishes while `toBlob` is in
    // flight, the frame id moves and we can no longer vouch for the captured
    // pixels — even a swap to another image with the same verdict changes the id.
    const frameIdBeforeCapture = view.canvasFrameId;
    const blob = await captureViewportPng(view);
    if (view.canvasFrameId !== frameIdBeforeCapture) {
        throw new BurnedInAnnotationError(
            'Viewport screenshot refused: the displayed image changed while the ' +
                'viewport was being captured, so the captured pixels could not ' +
                'be confirmed free of burned-in patient information.'
        );
    }
    if (!blob) {
        throw new Error(
            'Viewport screenshot capture produced no image: the canvas could not be serialised to a PNG blob'
        );
    }
    const url = URL.createObjectURL(blob);
    try {
        downloadCreatedFile(url, viewportScreenshotFilename(options));
    } finally {
        URL.revokeObjectURL(url);
    }
}
